import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../api';

export default function InvoiceForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [form, setForm] = useState({
    invoice_ref: '', invoice_date: '', sow_id: '', msa_id: '', customer_id: '', client_name: '',
    billing_model: '', billing_term: '', agreed_currency: 'USD', invoice_currency: 'USD',
    resource_name: '', billing_rate: 0, payment_terms: 30, due_date: '',
    forex_rate: 1, period_from: '', period_to: '', std_days_hrs: 22, actual_worked: 0,
    calculated_amt: 0, adjustment: 0, subtotal: 0, gst_rate_pct: 0, tax_amount: 0, total: 0,
    notes: '', status: 'Draft'
  });
  const [sows, setSows] = useState([]);

  useEffect(() => {
    api.get('/sow').then(setSows);
    if (id) {
      api.get(`/invoices/${id}`).then(data => setForm({ ...form, ...data }));
    }
  }, [id]);

  const set = (k, v) => setForm(f => {
    const updated = { ...f, [k]: v };
    // Recalculate amounts
    if (['billing_rate', 'std_days_hrs', 'actual_worked', 'adjustment', 'forex_rate', 'gst_rate_pct'].includes(k)) {
      const rate = parseFloat(updated.billing_rate) || 0;
      const std = parseFloat(updated.std_days_hrs) || 1;
      const actual = parseFloat(updated.actual_worked) || 0;
      const adj = parseFloat(updated.adjustment) || 0;
      const forex = parseFloat(updated.forex_rate) || 1;
      const gstPct = parseFloat(updated.gst_rate_pct) || 0;
      const calcAmt = (rate * actual / std);
      const subtotal = (calcAmt + adj) * forex;
      const taxAmt = subtotal * gstPct / 100;
      updated.calculated_amt = Math.round(calcAmt * 100) / 100;
      updated.subtotal = Math.round(subtotal * 100) / 100;
      updated.tax_amount = Math.round(taxAmt * 100) / 100;
      updated.total = Math.round((subtotal + taxAmt) * 100) / 100;
    }
    // Auto calculate due date
    if (['invoice_date', 'payment_terms'].includes(k) && updated.invoice_date && updated.payment_terms) {
      const d = new Date(updated.invoice_date);
      d.setDate(d.getDate() + parseInt(updated.payment_terms));
      updated.due_date = d.toISOString().split('T')[0];
    }
    return updated;
  });

  const handleSowSelect = async (sowId) => {
    if (!sowId) return;
    try {
      const s = await api.get(`/invoices/autofill/${sowId}`);
      setForm(f => ({
        ...f, sow_id: sowId, msa_id: s.msa_id, customer_id: s.customer_id,
        client_name: s.client_name, billing_model: s.billing_model, billing_term: s.billing_term,
        agreed_currency: s.agreed_currency, invoice_currency: s.invoicing_currency,
        resource_name: s.resource_name, billing_rate: s.billing_rate,
        payment_terms: s.payment_terms || 30, std_days_hrs: s.billing_term === 'Hourly' ? s.std_working_hours : 22
      }));
    } catch (e) { alert(e.message); }
  };

  const handleSave = async () => {
    try {
      if (isEdit) await api.put(`/invoices/${id}`, form);
      else await api.post('/invoices', form);
      navigate('/invoices');
    } catch (e) { alert(e.message); }
  };

  return (
    <>
      <div className="page-header">
        <h1>{isEdit ? `Invoice: ${id}` : 'New Invoice'}</h1>
        <div className="btn-group">
          <button className="btn btn-primary" onClick={handleSave}>Save Invoice</button>
        </div>
      </div>
      <div className="page-content">
        <nav className="page-back-nav" aria-label="Back navigation">
          <button type="button" className="page-back-link" onClick={() => navigate('/invoices')}>
            ← Back to Billing
          </button>
        </nav>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, alignItems: 'start' }}>
          <div>
            <div className="card">
              <div className="card-header">Invoice Header</div>
              <div className="form-grid">
                <div className="form-group"><label>SOW *</label>
                  <select value={form.sow_id} onChange={e => handleSowSelect(e.target.value)}>
                    <option value="">Select SOW</option>
                    {sows.map(s => <option key={s.sow_id} value={s.sow_id}>{s.sow_id} - {s.resource_name} ({s.client_name})</option>)}
                  </select>
                </div>
                <div className="form-group"><label>Reference Number</label><input value={form.invoice_ref} onChange={e => set('invoice_ref', e.target.value)} /></div>
                <div className="form-group"><label>Invoice Date</label><input type="date" value={form.invoice_date} onChange={e => set('invoice_date', e.target.value)} /></div>
                <div className="form-group"><label>MSA ID</label><input value={form.msa_id} readOnly /></div>
                <div className="form-group"><label>Customer ID</label><input value={form.customer_id} readOnly /></div>
                <div className="form-group"><label>Client Name</label><input value={form.client_name} readOnly /></div>
                <div className="form-group"><label>Billing Model</label><input value={form.billing_model} readOnly /></div>
                <div className="form-group"><label>Billing Term</label><input value={form.billing_term} readOnly /></div>
                <div className="form-group"><label>Agreed Currency</label><input value={form.agreed_currency} readOnly /></div>
                <div className="form-group"><label>Invoice Currency</label><input value={form.invoice_currency} readOnly /></div>
                <div className="form-group"><label>Resource</label><input value={form.resource_name} readOnly /></div>
                <div className="form-group"><label>Billing Rate</label><input value={form.billing_rate} readOnly /></div>
                <div className="form-group"><label>Payment Terms (days)</label><input value={form.payment_terms} readOnly /></div>
                <div className="form-group"><label>Due Date</label><input value={form.due_date} readOnly /></div>
              </div>
            </div>

            <div className="card">
              <div className="card-header">Manual Inputs</div>
              <div className="form-grid">
                <div className="form-group"><label>Forex Rate</label><input type="number" step="0.01" value={form.forex_rate} onChange={e => set('forex_rate', e.target.value)} /></div>
                <div className="form-group"><label>Period From</label><input type="date" value={form.period_from} onChange={e => set('period_from', e.target.value)} /></div>
                <div className="form-group"><label>Period To</label><input type="date" value={form.period_to} onChange={e => set('period_to', e.target.value)} /></div>
                <div className="form-group"><label>Std Days/Hrs</label><input type="number" value={form.std_days_hrs} onChange={e => set('std_days_hrs', e.target.value)} /></div>
                <div className="form-group"><label>Actual Worked</label><input type="number" step="0.5" value={form.actual_worked} onChange={e => set('actual_worked', e.target.value)} /></div>
                <div className="form-group"><label>Adjustment</label><input type="number" step="0.01" value={form.adjustment} onChange={e => set('adjustment', e.target.value)} /></div>
                <div className="form-group"><label>GST Rate %</label><input type="number" step="0.1" value={form.gst_rate_pct} onChange={e => set('gst_rate_pct', e.target.value)} /></div>
                <div className="form-group full-width"><label>Notes</label><textarea value={form.notes} onChange={e => set('notes', e.target.value)} /></div>
                <div className="form-group"><label>Status</label>
                  <select value={form.status} onChange={e => set('status', e.target.value)}>
                    <option>Draft</option><option>Pending</option><option>Paid</option><option>Overdue</option><option>Cancelled</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Invoice Preview Card */}
          <div style={{ position: 'sticky', top: 80 }}>
            <div className="agreement-preview" style={{ fontFamily: "'Segoe UI', sans-serif" }}>
              <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <h1 style={{ fontFamily: "'Segoe UI', sans-serif", fontSize: 18, marginBottom: 4 }}>INVOICE</h1>
                <div style={{ fontSize: 12, color: '#666' }}>{isEdit ? id : '(Auto-generated on save)'}</div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20, fontSize: 12 }}>
                <div>
                  <strong>From:</strong><br />
                  Colan Infotech Pvt. Ltd.<br />
                  Chennai, India
                </div>
                <div style={{ textAlign: 'right' }}>
                  <strong>To:</strong><br />
                  {form.client_name || '---'}<br />
                  {form.customer_id}
                </div>
              </div>

              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 16 }}>
                <tbody>
                  <tr><td style={{ padding: '4px 0', borderBottom: '1px solid #eee' }}>Invoice Date</td><td style={{ textAlign: 'right', padding: '4px 0', borderBottom: '1px solid #eee' }}>{form.invoice_date || '---'}</td></tr>
                  <tr><td style={{ padding: '4px 0', borderBottom: '1px solid #eee' }}>SOW ID</td><td style={{ textAlign: 'right', padding: '4px 0', borderBottom: '1px solid #eee' }}>{form.sow_id || '---'}</td></tr>
                  <tr><td style={{ padding: '4px 0', borderBottom: '1px solid #eee' }}>MSA ID</td><td style={{ textAlign: 'right', padding: '4px 0', borderBottom: '1px solid #eee' }}>{form.msa_id || '---'}</td></tr>
                  <tr><td style={{ padding: '4px 0', borderBottom: '1px solid #eee' }}>Period</td><td style={{ textAlign: 'right', padding: '4px 0', borderBottom: '1px solid #eee' }}>{form.period_from} to {form.period_to}</td></tr>
                  <tr><td style={{ padding: '4px 0', borderBottom: '1px solid #eee' }}>Due Date</td><td style={{ textAlign: 'right', padding: '4px 0', borderBottom: '1px solid #eee' }}>{form.due_date || '---'}</td></tr>
                </tbody>
              </table>

              <div style={{ background: '#f7fafc', padding: 12, borderRadius: 4, marginBottom: 16 }}>
                <div style={{ fontWeight: 700, marginBottom: 8, fontSize: 13 }}>Billing Calculation</div>
                <table style={{ width: '100%', fontSize: 12 }}>
                  <tbody>
                    <tr><td>Resource</td><td style={{ textAlign: 'right' }}>{form.resource_name}</td></tr>
                    <tr><td>Rate ({form.billing_term})</td><td style={{ textAlign: 'right' }}>{form.agreed_currency} {parseFloat(form.billing_rate || 0).toLocaleString()}</td></tr>
                    <tr><td>Std Days/Hrs</td><td style={{ textAlign: 'right' }}>{form.std_days_hrs}</td></tr>
                    <tr><td>Actual Worked</td><td style={{ textAlign: 'right' }}>{form.actual_worked}</td></tr>
                    <tr style={{ borderTop: '1px solid #ddd' }}><td>Calculated Amount</td><td style={{ textAlign: 'right' }}>{form.invoice_currency} {form.calculated_amt?.toLocaleString()}</td></tr>
                    <tr><td>+ Adjustment</td><td style={{ textAlign: 'right' }}>{form.adjustment}</td></tr>
                    <tr><td>x Forex Rate</td><td style={{ textAlign: 'right' }}>{form.forex_rate}</td></tr>
                    <tr style={{ borderTop: '1px solid #ddd', fontWeight: 600 }}><td>Subtotal</td><td style={{ textAlign: 'right' }}>{form.invoice_currency} {form.subtotal?.toLocaleString()}</td></tr>
                    <tr><td>Tax ({form.gst_rate_pct}%)</td><td style={{ textAlign: 'right' }}>{form.invoice_currency} {form.tax_amount?.toLocaleString()}</td></tr>
                  </tbody>
                </table>
              </div>

              <div style={{ background: 'var(--primary)', color: '#fff', padding: '12px 16px', borderRadius: 4, display: 'flex', justifyContent: 'space-between', fontSize: 16, fontWeight: 700 }}>
                <span>TOTAL</span>
                <span>{form.invoice_currency} {form.total?.toLocaleString()}</span>
              </div>

              {form.notes && <div style={{ marginTop: 16, fontSize: 12, color: '#666' }}><strong>Notes:</strong> {form.notes}</div>}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
